import type { CityConfig } from "../city-template/types";

export const richmondConfig: CityConfig = {
  city: "Richmond",
  state: "Kentucky",
  stateAbbr: "KY",
  slug: "richmond",
  route: "/richmond",
  title: "Emergency Passport Richmond, KY — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Richmond, KY — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Madison County and Eastern Kentucky travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Richmond, KY",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Richmond and Madison County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Richmond%20Kentucky%20downtown%20historic%20Main%20Street%20with%20charming%20brick%20storefronts%20and%20Madison%20County%20courthouse%2C%20Eastern%20Kentucky%20university%20town%20atmosphere%20with%20warm%20golden%20hour%20light%2C%20I-75%20corridor%20small%20city%20with%20tree-lined%20streets%20and%20rolling%20green%20hills%2C%20clean%20modern%20cityscape%20photography%2C%20wide%20panoramic%20view%2C%20no%20visible%20text%20or%20people%2C%20vibrant%20Madison%20County%20scene%20with%20pastoral%20countryside%20beyond%20the%20downtown&width=1800&height=700&seq=richmond-hero-001&orientation=landscape",
  heroImageAlt: "Richmond Kentucky downtown historic Main Street with Madison County courthouse",
  geoPosition: "37.7479;-84.2947",
  geoPlacename: "Richmond, Kentucky",
  regionalAgency: {
    name: "Atlanta Passport Agency",
    address: "230 Peachtree St NW, Suite 1000, Atlanta, GA 30308",
    distance: "Located in downtown Atlanta — approximately 320 miles from Richmond via I-75",
  },
  nearbyCitiesZips: [
    { name: "Richmond", zips: "40475, 40476" },
    { name: "Berea", zips: "40403, 40404" },
    { name: "Lexington", zips: "40502–40517" },
    { name: "Nicholasville", zips: "40356, 40340" },
    { name: "Winchester", zips: "40391, 40392" },
    { name: "Mount Sterling", zips: "40353" },
    { name: "Danville", zips: "40422, 40423" },
    { name: "Harrodsburg", zips: "40330" },
    { name: "Frankfort", zips: "40601, 40604" },
    { name: "Georgetown", zips: "40324" },
    { name: "Versailles", zips: "40383, 40384" },
    { name: "Lancaster", zips: "40444" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Madison County departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Lexington", route: "/lexington" },
    { name: "Fast passport renewal in Louisville", route: "/louisville" },
    { name: "Emergency passport help in Berea", route: "/cities/berea" },
    { name: "Urgent passport services in Frankfort", route: "/frankfort" },
    { name: "Same-day passport help in Winchester", route: "/winchester" },
    { name: "Passport courier services near Georgetown", route: "/georgetown" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Richmond remotely via secure courier. We do not have a walk-in office in Richmond — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Madison County travelers.",
  fpcOfficeAddress: "312 Walnut St, Cincinnati, OH 45202",
  fpcOfficeDistance: "Serving all of Madison County and the Eastern Kentucky I-75 corridor",
  serviceAreaIntro: "From downtown Richmond's historic district to the Eastern Kentucky University campus, from the Madison County countryside to the I-75 corridor — Fast Passport Center serves the entire Richmond community. We regularly help travelers from Berea, Lexington, and Nicholasville get their passports fast.",
  nearbyCitiesIntro: "We serve all Richmond neighborhoods and nearby Eastern Kentucky cities including Berea, Lexington, Nicholasville, Winchester, Mount Sterling, Danville, Harrodsburg, Frankfort, Georgetown, Versailles, and Lancaster. If you are in Madison County, the I-75 corridor, or the greater Lexington area, we have you covered.",
  howItWorksIntro: "Most Richmond travelers complete everything online and ship documents overnight. No need to drive to Atlanta or wait in line at the regional agency.",
  mistakesIntro: "Richmond travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Richmond and Madison County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Richmond? Here are the most common ones we hear from Madison County travelers.",
  statePageRoute: "/states/kentucky",
  statePageName: "Kentucky",
  dmvWebsite: "https://drive.ky.gov",
  dmvLabel: "Kentucky Transportation Cabinet — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Madison County international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Ashley K.",
      location: "Richmond, KY",
      rating: 5,
      text: "My EKU study abroad semester in London was starting in 4 days and I realized my passport was expired. Fast Passport Center got my renewal in 48 hours. I made my flight and had the semester of my dreams.",
      service: "Passport Renewal",
    },
    {
      name: "Marcus J.",
      location: "Berea, KY",
      rating: 5,
      text: "Emergency family trip to Mexico came up with 2 days notice. They got my passport in 24 hours. I didn't have to drive to Atlanta or Nashville — just shipped from the Richmond UPS store.",
      service: "Emergency Passport",
    },
    {
      name: "Lisa T.",
      location: "Nicholasville, KY",
      rating: 5,
      text: "First-time passport for our anniversary cruise to the Caribbean. The team helped me with every step and I had my passport in 5 days. We had the best trip celebrating 25 years.",
      service: "New Passport",
    },
    {
      name: "Greg H.",
      location: "Winchester, KY",
      rating: 5,
      text: "Lost my passport the morning of my flight to Paris. I was absolutely panicked. They got me an emergency replacement in 24 hours. I walked onto the plane with a new passport. Incredible.",
      service: "Lost Passport",
    },
  ],
  facilities: [
    {
      id: "richmond-1",
      name: "Richmond Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "140 N Madison Ave",
      city: "Richmond",
      state: "KY",
      zip: "40475",
      phone: "(859) 623-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.2 miles from downtown Richmond",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for Madison County. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "richmond-2",
      name: "Berea Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "101 N Broadway",
      city: "Berea",
      state: "KY",
      zip: "40403",
      phone: "(859) 986-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles from Richmond",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Madison County and southern Berea. Photo services available. Book ahead.",
      icon: "ri-mail-line",
    },
    {
      id: "richmond-3",
      name: "Madison County Clerk",
      type: "County Clerk Passport Acceptance",
      address: "135 W Irvine St",
      city: "Richmond",
      state: "KY",
      zip: "40475",
      phone: "(859) 624-4700",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "0.1 miles from Richmond Courthouse",
      website: "https://www.madisoncounty.com/",
      websiteLabel: "Madison County Clerk",
      notes: "Major county facility for Madison County. Accepts walk-ins but appointments recommended for faster service.",
      icon: "ri-government-line",
    },
    {
      id: "richmond-4",
      name: "Lexington Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1055 New Circle Rd",
      city: "Lexington",
      state: "KY",
      zip: "40511",
      phone: "(859) 252-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "30 miles from Richmond",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Major facility for the Lexington area. High volume — book appointments 2+ weeks in advance.",
      icon: "ri-mail-line",
    },
    {
      id: "richmond-5",
      name: "Winchester Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "120 N Main St",
      city: "Winchester",
      state: "KY",
      zip: "40391",
      phone: "(859) 744-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "20 miles from Richmond",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Clark County and northern Madison County. Limited weekend hours.",
      icon: "ri-mail-line",
    },
    {
      id: "richmond-6",
      name: "Nicholasville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "100 Industry Pkwy",
      city: "Nicholasville",
      state: "KY",
      zip: "40356",
      phone: "(859) 885-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "35 miles from Richmond",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Jessamine County and southern Madison County. Full passport services with appointment.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5 hour drive to Atlanta", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Madison County photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Madison County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Richmond applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Atlanta regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Madison County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Madison County travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Richmond." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Richmond address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Richmond before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Richmond for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 5 hour drive to Atlanta." },
    { q: "How fast can I renew my passport in Richmond?", a: "Fast Passport Center offers fast passport renewal in Richmond in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "What if my passport expires before my EKU study abroad program?", a: "If your passport expires before your study abroad program, contact Fast Passport Center immediately. Many Eastern Kentucky University students travel internationally. We can expedite your passport renewal in Richmond in 24–48 hours so you don't miss your program start date." },
    { q: "Can I get a same-day passport appointment near Richmond?", a: "Same-day passport appointments are only available at the Atlanta Regional Passport Agency — a 5 hour drive from Richmond — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Madison County residents." },
    { q: "Can I expedite a child passport in Richmond?", a: "Yes. Fast Passport Center expedites child passports in Richmond for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Madison County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Richmond with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "How fast can I get a passport in Richmond?", a: "With Fast Passport Center's emergency courier service, Richmond travelers can receive their passport in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities, bypassing standard mail delays." },
    { q: "Do I need to drive to the Atlanta Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Where are the closest passport acceptance facilities in Richmond?", a: "Popular options include the Richmond Post Office (40475), Madison County Clerk (40475), Berea Post Office (40403), Lexington Main Post Office (40511), Winchester Post Office (40391), and Nicholasville Post Office (40356). All require appointments. We help you find the most convenient location and schedule your visit." },
    { q: "Can I get a same-day passport in Richmond?", a: "Same-day passports are only available at the Atlanta Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency processing is the fastest reliable option — without the 5 hour drive to Atlanta." },
    { q: "What if I live in a smaller Madison County town?", a: "We serve the entire Madison County and surrounding area including Berea, Lexington, Nicholasville, Winchester, Mount Sterling, Danville, Harrodsburg, Frankfort, Georgetown, Versailles, and Lancaster. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed." },
    { q: "How much does expedited passport service cost in Richmond?", a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline." },
    { q: "Do you help with passport photos in Richmond?", a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and Walmart locations in Richmond and Lexington offer passport photo services. We review your photos before submission to ensure they meet State Department standards." },
    { q: "What documents do I need for a first-time passport in Richmond?", a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk." },
    { q: "Can you help with a child's passport in Richmond?", a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Madison County families through the entire process and help schedule acceptance clerk appointments." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Richmond", address: "2100 Lantern Ridge Dr, Richmond, KY 40475", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Richmond", address: "803 Eastern Bypass, Richmond, KY 40475", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Richmond", address: "820 Eastern Bypass, Richmond, KY 40475", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Richmond", address: "2000 Lantern Ridge Dr, Richmond, KY 40475", hours: "Mon–Sun: 8AM–10PM" },
    { name: "CVS Pharmacy — Lexington", address: "3151 Richmond Rd, Lexington, KY 40509", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walmart — Lexington", address: "500 W New Circle Rd, Lexington, KY 40511", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Walgreens — Berea", address: "1001 N Broadway, Berea, KY 40403", hours: "Mon–Sun: 7AM–11PM" },
    { name: "FedEx Office — Lexington", address: "1650 Bryan Station Rd, Lexington, KY 40505", hours: "Mon–Fri: 7AM–9PM, Sat: 8AM–6PM" },
  ],
  dmvLocations: [
    {
      name: "Circuit Court Clerk — Madison County",
      address: "135 W Irvine St, Richmond, KY 40475",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Driver's license and REAL ID. Appointment recommended.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Madison County",
      address: "212 S Main St, Berea, KY 40403",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative for southern Madison County and Berea residents.",
      featured: false,
    },
    {
      name: "Circuit Court Clerk — Clark County",
      address: "34 S Main St, Winchester, KY 40391",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center for Clark County and northern Madison County residents.",
      featured: true,
    },
    {
      name: "Circuit Court Clerk — Fayette County",
      address: "162 E Main St, Lexington, KY 40507",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Alternative for Fayette County and western Madison County residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "LEX",
      name: "Blue Grass Airport",
      distance: "30 miles from Richmond",
      address: "4000 Terminal Dr, Lexington, KY 40510",
      note: "The primary airport for Madison County. Regional and limited international flights.",
      flightUrl: "https://www.bluegrassairport.com/",
      flightLabel: "Lexington Airport",
      featured: true,
    },
    {
      code: "SDF",
      name: "Louisville Muhammad Ali International",
      distance: "95 miles from Richmond",
      address: "600 Terminal Dr, Louisville, KY 40209",
      note: "Major international hub for long-haul flights to Europe, Asia, and the Caribbean. Accessible via I-75.",
      flightUrl: "https://www.flylouisville.com/",
      flightLabel: "Louisville Airport",
      featured: false,
    },
    {
      code: "CVG",
      name: "Cincinnati/Northern Kentucky International",
      distance: "85 miles from Richmond",
      address: "3087 Terminal Dr, Hebron, KY 41048",
      note: "Alternative major international hub for international flights. Accessible via I-75.",
      flightUrl: "https://www.cvgairport.com/",
      flightLabel: "CVG Airport",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Birth & Death Certificates", desc: "Kentucky Office of Vital Statistics in Frankfort — order certified copies online, by mail, or in person for passport applications and name changes.", url: "https://www.chfs.ky.gov/agencies/dph/dehp/vsb/Pages/default.aspx" },
    { icon: "ri-heart-line", label: "Marriage Records", desc: "Madison County Clerk — certified marriage licenses for name change passport applications and legal documentation.", url: "https://www.madisoncountyky.us/" },
    { icon: "ri-scales-line", label: "Divorce Decrees", desc: "Madison Circuit Court Clerk — certified divorce decrees supporting passport name change or correction.", url: "https://kycourts.gov/Courts/Circuit-Court/Pages/Madison.aspx" },
    { icon: "ri-global-line", label: "VitalChek — Nationwide Records", desc: "If your record was issued outside Kentucky, order certified copies online through VitalChek for delivery in 5–10 business days.", url: "https://www.vitalchek.com/" },
  ],
};